import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ViewChild,
} from '@angular/core';
import { StatusOption } from '../../flag-manager.interfaces';
import { FlagGroup, UnderwritingStatus } from '../../../../shared/interfaces';
import { FlagManagerService } from '../../flag-manager.service';
import { FlagService } from '../../../../flag/flag.service';
import { FlagGroupService } from '../../../../flag/flag.group.service';
import { ToastrService } from 'ngx-toastr';
import Sortable from 'sortablejs';

@Component({
  selector: 'app-flag-manager-sidebar',
  templateUrl: './flag-manager-sidebar.component.html',
  styleUrls: ['./flag-manager-sidebar.component.scss'],
})
export class FlagManagerSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() readonly statuses: StatusOption[] = [];
  @Input() groups: any[];
  @Input() countUncategorized: number;
  @Input() comments: number;
  @Input() selectedGroup: any;
  @Input() underwritingStatus: UnderwritingStatus;
  @Input() inspectionId: number;
  @Output() showComments = new EventEmitter<any>();
  @Output() openSelectedGroup = new EventEmitter<FlagGroup>();
  @Output() setIssueNo = new EventEmitter<number>();
  @Output() removeGroup = new EventEmitter<number>();
  @Output() createGroup = new EventEmitter<string>();
  @Output() openGroupModal = new EventEmitter<void>();
  @Output() flagDropped = new EventEmitter<{
    flagId: number;
    targetGroupId: number | null;
    revert: () => void;
  }>();
  @Output() dropReverted = new EventEmitter<void>();
  @ViewChildren('dropTarget') dropTargets!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('includedList', { static: false }) includedListRef!: ElementRef<HTMLDivElement>;
  @ViewChild('excludedList', { static: false }) excludedListRef!: ElementRef<HTMLDivElement>;
  excludedGroups: any[] = [];
  includedGroups: any[] = [];
  private sortables: Sortable[] = [];
  private groupSortables: Sortable[] = [];

  typeMap = {
    2: 'grey-flag.svg',
    3: 'red-flag.svg',
  };

  constructor(
    private flagManagerService: FlagManagerService,
    private flagService: FlagService,
    private flagGroupService: FlagGroupService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.flagManagerService.getExcludedGroups().subscribe(groups => (this.excludedGroups = groups));
    this.flagManagerService.getIncludedGroups().subscribe(groups => (this.includedGroups = groups));
  }

  ngAfterViewInit(): void {
    this.initDropTargets();
    this.initGroupSortables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groups'] || changes['underwritingStatus']) {
      if (changes['groups']) {
        this.flagManagerService.separateGroups(this.groups);
      }
      setTimeout(() => {
        this.initDropTargets();
        this.initGroupSortables();
      });
    }

    if (changes['selectedGroup']) {
      this.selectedGroupChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyDropTargets();
    this.destroyGroupSortables();
  }

  openGroup(group: any, issueNo?: number) {
    this.selectedGroup = group;
    this.openSelectedGroup.emit(group);

    if (issueNo != null) {
      this.setIssueNo.emit(issueNo);
    }
  }

  private initDropTargets(): void {
    this.destroyDropTargets();
    if (!this.dropTargets) return;

    this.dropTargets.forEach(ref => {
      const el = ref.nativeElement;

      // Ensure element is in the DOM
      if (!el.isConnected) {
        return;
      }

      const sortable = Sortable.create(el, {
        group: { name: 'flags-xfer', pull: false, put: true },
        sort: false,
        animation: 0, // Disable animation for better performance on Mac
        forceFallback: true, // Use mouse events instead of native drag for better Mac performance
        fallbackTolerance: 0, // Immediate fallback
        swapThreshold: 0.65, // Better swap detection
        ghostClass: 'hidden-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        // Performance optimizations
        delay: 0,
        scroll: false,
        scrollSensitivity: 0,
        scrollSpeed: 0,
        bubbleScroll: false,
        preventOnFilter: false,
        dragoverBubble: false,
        removeCloneOnHide: true,
        emptyInsertThreshold: 5,
        onAdd: evt => {
          const itemEl = evt.item as HTMLElement;
          const flagIdStr = itemEl.dataset.flagId;
          const toEl = evt.to as HTMLElement;
          const groupIdAttr = toEl.dataset.groupId;
          const targetGroupId =
            groupIdAttr && groupIdAttr !== 'null' && groupIdAttr !== '' ? Number(groupIdAttr) : null;
          const flagId = flagIdStr ? Number(flagIdStr) : NaN;

          if (isNaN(flagId)) {
            // Invalid flag, remove from DOM
            itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);
            return;
          }

          const fromEl = evt.from as HTMLElement;
          const oldIndex = evt.oldIndex ?? -1;
          const originalContainer = fromEl;

          const revert = () => {
            if (itemEl.parentNode) {
              itemEl.parentNode.removeChild(itemEl);
            }

            if (originalContainer) {
              const children = Array.from(originalContainer.children);
              const actualChildren = children.filter(
                child =>
                  child.classList.contains('image-card') ||
                  child.hasAttribute('data-flag-id') ||
                  (child as HTMLElement).dataset.flagId,
              );

              if (oldIndex >= 0 && oldIndex < actualChildren.length) {
                const targetChild = actualChildren[oldIndex];
                originalContainer.insertBefore(itemEl, targetChild);
              } else if (actualChildren.length > 0) {
                originalContainer.insertBefore(itemEl, actualChildren[0]);
              } else {
                originalContainer.appendChild(itemEl);
              }
            }
          };

          // Check if status is "Rec Letter Sent" or beyond - show warning and revert
          if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) {
            this.toastr.warning('Moving flags is disabled for this status');
            itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);
            revert();
            // Emit event so parent can reload to resync Sortable.js state
            this.dropReverted.emit();
            return;
          }

          itemEl.parentNode && itemEl.parentNode.removeChild(itemEl);

          this.flagDropped.emit({ flagId, targetGroupId, revert });
        },
      });
      this.sortables.push(sortable);
    });
  }

  private destroyDropTargets(): void {
    this.sortables.forEach(s => s.destroy());
    this.sortables = [];
  }

  private initGroupSortables(): void {
    this.destroyGroupSortables();

    // Group reordering is always allowed (regardless of status)
    // Included list: reorder only, no cross-list
    if (this.includedListRef?.nativeElement) {
      const s = Sortable.create(this.includedListRef.nativeElement, {
        group: { name: 'included-groups', pull: false, put: false },
        sort: true,
        animation: 0, // Disable animation for better performance on Mac
        forceFallback: true, // Use mouse events instead of native drag for better Mac performance
        fallbackTolerance: 0, // Immediate fallback
        swapThreshold: 0.65, // Better swap detection
        draggable: '.flag',
        handle: '.cta .icon',
        ghostClass: 'group-drag-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: () => {
          // persistGroupOrder will check internally if order actually changed
          this.persistGroupOrder();
        },
      });
      this.groupSortables.push(s);
    }
  }

  private destroyGroupSortables(): void {
    this.groupSortables.forEach(s => s.destroy());
    this.groupSortables = [];
  }

  private persistGroupOrder(): void {
    const includedIds = this.readIdsFromList(this.includedListRef);

    const newOrder = [...includedIds];
    if (!newOrder.length) return;

    // Get current order from the groups arrays
    const currentIncludedIds = this.includedGroups.map(g => g.id);
    const currentOrder = [...currentIncludedIds];

    // Check if order has actually changed
    const hasOrderChanged =
      newOrder.length !== currentOrder.length || newOrder.some((id, index) => id !== currentOrder[index]);

    if (!hasOrderChanged) {
      // Order hasn't changed, no need to update
      return;
    }

    setTimeout(() => {
      this.selectedGroupChanges();
    }, 0);

    const groupsMap = new Map(this.groups.map(g => [g.id, g]));
    const reorderedGroups = newOrder.map(id => groupsMap.get(id)).filter(Boolean);
    const remainingGroups = this.groups.filter(g => !newOrder.includes(g.id));
    const finalGroups = [...reorderedGroups, ...remainingGroups];

    this.flagManagerService.separateGroups(finalGroups);

    this.flagGroupService
      .updateOrder(this.inspectionId, newOrder)
      .then(() => {
        this.toastr.success('You have successfully reordered the groups');
      })
      .catch(() => {
        this.toastr.error('Failed to update group order');
      });
  }

  private readIdsFromList(ref?: ElementRef<HTMLDivElement>): number[] {
    if (!ref?.nativeElement) return [];
    const items = Array.from(ref.nativeElement.querySelectorAll('.flag[data-group-id]')) as HTMLElement[];
    return items.map(el => Number(el.dataset.groupId)).filter(id => Number.isFinite(id));
  }

  selectedGroupChanges(): void {
    if (!this.selectedGroup || !Array.isArray(this.includedGroups)) return;
    const index = this.includedGroups.findIndex(group => group.id === this.selectedGroup.id);
    if (index !== -1) {
      this.setIssueNo.emit(index + 1);
    }
  }

  openConfirmationModal(group, index) {
    this.openGroup(group, index);
    this.removeGroup.emit(this.selectedGroup);
  }

  isChecked(group: any) {
    const found = this.groups?.find(g => g.id === group.id);
    return found?.checkbox?.value === true;
  }

  anyCheckboxChecked() {
    return this.groups?.some(flag => flag.checkbox?.value);
  }

  isGroupComplete(group: any): boolean {
    if (!group) return false;
    return this.flagService.isCompleted(group);
  }

  getGroupIcon(group: any): string {
    if (this.isGroupComplete(group)) {
      return 'assets/img/check.svg';
    }
    return 'assets/img/svg/group-detail.svg';
  }

  onAddGroup() {
    this.openGroupModal.emit();
  }

  onCreateGroup(groupName: string) {
    this.createGroup.emit(groupName);
  }
}
